import { toRaw } from 'vue'
import { defineStore } from 'pinia'
import {
  createMyWeekEntry,
  deleteMyWeekEntry,
  getMyWeek,
  getMyWeekSuggestions,
  handoverMyWeekEntry,
  reorderMyWeek,
  setMyWeekEntryStatus,
  updateMyWeekEntry,
  upsertMyWeekPlan,
  type UpsertMyWeekPlanPayload,
} from '@/api/v2/my_week'
import { upsert as upsertTask } from '@/api/v2/tasks'
import type { MyWeek, MyWeekEntry, MyWeekSuggestion, MyWeekTarget } from '@/models/MyWeek'
import { partition, type MyWeekGroups } from '@/views/my-week/lib/partition'

/**
 * Every request that answers with the whole week takes a number; only the answer of the latest one
 * is applied, so a slow reload cannot overwrite a newer state.
 */
let weekSeq = 0

interface MyWeekState {
  data: MyWeek | null
  suggestions: MyWeekSuggestion[]
  suggestionsLoaded: boolean
  loading: boolean
  loadFailed: boolean
}

type EntryList = 'entries' | 'next_week' | 'carry_over'
const LISTS: EntryList[] = ['entries', 'next_week', 'carry_over']

/**
 * The "Meine Woche" page: the week of the current user, the suggestions and every action on an
 * entry. Actions answer with the changed entry or the whole week; the capacity is recomputed by the
 * backend, so actions that change it reload the week quietly.
 */
export const useMyWeekStore = defineStore('myWeek', {
  state: (): MyWeekState => ({
    data: null,
    suggestions: [],
    suggestionsLoaded: false,
    loading: false,
    loadFailed: false,
  }),

  getters: {
    groups(state): MyWeekGroups | null {
      if (!state.data) return null
      return partition(state.data.entries, state.data.week.today)
    },
    findEntry(state) {
      return (id: string): MyWeekEntry | null => {
        if (!state.data) return null
        for (const list of LISTS) {
          const hit = state.data[list].find((entry) => entry.id === id)
          if (hit) return hit
        }
        return null
      }
    },
  },

  actions: {
    resetState() {
      this.data = null
      this.suggestions = []
      this.suggestionsLoaded = false
      this.loading = false
      this.loadFailed = false
      weekSeq++
    },

    /** Loads a week ("current" when omitted). */
    async load(week?: string | null): Promise<MyWeek | null> {
      this.loading = true
      const seq = ++weekSeq
      try {
        const resp = await getMyWeek(week ?? null)
        this.setWeek(resp.data.data, seq)
        this.loadFailed = false
        return this.data
      } catch (err) {
        this.loadFailed = true
        throw err
      } finally {
        this.loading = false
      }
    },

    /** Reloads the shown week without the loading state, e.g. for new capacity numbers. */
    async refresh(): Promise<void> {
      const week = this.data && !this.data.week.is_current ? this.data.week.iso : null
      const seq = ++weekSeq
      const resp = await getMyWeek(week)
      this.setWeek(resp.data.data, seq)
    },

    /** refresh() after a successful change: a failed reload must not turn the change into an error. */
    async quietRefresh(): Promise<void> {
      try {
        await this.refresh()
      } catch {
        // The change went through; the numbers update with the next load.
      }
    },

    async loadSuggestions(): Promise<MyWeekSuggestion[]> {
      const resp = await getMyWeekSuggestions()
      this.suggestions = resp.data.data
      this.suggestionsLoaded = true
      return this.suggestions
    },

    /** Applies a whole week, unless a newer request (`seq`) has been started since. */
    setWeek(week: MyWeek, seq: number = weekSeq) {
      if (seq !== weekSeq) return
      this.data = week
    },

    /** Puts the entry from a response in place of the old one, in whichever list it is. */
    replaceEntry(entry: MyWeekEntry) {
      if (!this.data) return
      for (const list of LISTS) {
        const index = this.data[list].findIndex((e) => e.id === entry.id)
        if (index >= 0) {
          this.data[list].splice(index, 1, entry)
          return
        }
      }
    },

    dropEntry(id: string) {
      if (!this.data) return
      for (const list of LISTS) {
        this.data[list] = this.data[list].filter((e) => e.id !== id)
      }
    },

    /**
     * Plans a task (POST /entries). Planning a task that is planned already moves it. The backend
     * makes the user an assignee when they were not.
     */
    async addTask(
      task_id: string,
      options: { today?: boolean; beforeEntryId?: string | null; week?: MyWeekTarget; decided?: boolean } = {},
    ): Promise<MyWeekEntry> {
      const resp = await createMyWeekEntry({
        task_id,
        ...(options.week ? { week: options.week } : {}),
        ...(options.today ? { today: true } : {}),
        ...(options.beforeEntryId ? { before_entry_id: options.beforeEntryId } : {}),
        ...(options.decided ? { decided: true as const } : {}),
      })
      this.suggestions = this.suggestions.filter((s) => s.task.id !== task_id)
      await this.quietRefresh()
      return resp.data.data
    },

    /** Puts an entry on "Heute" or back into the week; optimistic, reverted on error. */
    async setToday(id: string, today: boolean): Promise<MyWeekEntry | null> {
      const entry = this.findEntry(id)
      if (!entry || !this.data) return null
      const before = { today: entry.today, today_date: entry.today_date }
      entry.today = today
      entry.today_date = today ? this.data.week.today : null
      try {
        const resp = await updateMyWeekEntry(id, { today })
        this.replaceEntry(resp.data.data)
        return resp.data.data
      } catch (err) {
        entry.today = before.today
        entry.today_date = before.today_date
        throw err
      }
    },

    /** Moves an entry into the current or the next week (the carry rules live in the backend). */
    async moveWeek(id: string, week: MyWeekTarget, options: { decided?: boolean } = {}): Promise<MyWeekEntry> {
      const resp = await updateMyWeekEntry(id, { week, ...(options.decided ? { decided: true as const } : {}) })
      this.replaceEntry(resp.data.data)
      await this.quietRefresh()
      return resp.data.data
    },

    /** "Bleibt diese Woche" — confirms an entry that was carried three times or more. */
    async decide(id: string): Promise<MyWeekEntry> {
      const resp = await updateMyWeekEntry(id, { decided: true })
      this.replaceEntry(resp.data.data)
      return resp.data.data
    },

    /** Ticks an entry off (own assignee status closed) or opens it again. */
    async setStatus(id: string, status: 'closed' | 'open'): Promise<MyWeekEntry> {
      const resp = await setMyWeekEntryStatus(id, status)
      this.replaceEntry(resp.data.data)
      await this.quietRefresh()
      return resp.data.data
    },

    /**
     * New order of the movable rows; ids in `today_ids` go on "Heute", ids in `week_ids` off it.
     * Optimistic: the list reorders at once and is restored when the request fails.
     */
    async reorder(today_ids: string[], week_ids: string[]): Promise<void> {
      if (!this.data) return
      const snapshot = this.data.entries
      const byId = new Map(snapshot.map((e) => [e.id, e]))
      const todayDate = this.data.week.today
      const moved = new Set([...today_ids, ...week_ids])
      const ordered: MyWeekEntry[] = [
        ...today_ids
          .map((id) => byId.get(id))
          .filter((e): e is MyWeekEntry => !!e)
          .map((e) => ({ ...e, today: e.state === 'planned', today_date: todayDate })),
        ...week_ids
          .map((id) => byId.get(id))
          .filter((e): e is MyWeekEntry => !!e)
          .map((e) => ({ ...e, today: false, today_date: null })),
        ...snapshot.filter((e) => !moved.has(e.id)),
      ]
      const optimistic = ordered.map((e, position) => ({ ...e, position }))
      this.data.entries = optimistic
      const seq = ++weekSeq

      try {
        const resp = await reorderMyWeek({ week: this.data.week.iso, today_ids, week_ids })
        this.setWeek(resp.data.data, seq)
      } catch (err) {
        // Only undo our own change: a newer state (another move, a reload) stays.
        if (this.data && toRaw(this.data.entries) === optimistic) this.data.entries = snapshot
        throw err
      }
    },

    /** Takes an entry out of the week; with `unassign` the user also stops being an assignee. */
    async remove(id: string, options: { unassign?: boolean } = {}): Promise<void> {
      await deleteMyWeekEntry(id, options)
      this.dropEntry(id)
      await this.quietRefresh()
    },

    /** Hands the task over to a colleague; the entry goes, the user stays a watcher. */
    async handover(id: string, user_id: string): Promise<void> {
      await handoverMyWeekEntry(id, user_id)
      this.dropEntry(id)
      await this.quietRefresh()
    },

    /** Saves the plan of the shown week (days off, appointments, "Woche starten"). */
    async savePlan(payload: Omit<UpsertMyWeekPlanPayload, 'week'>): Promise<MyWeek | null> {
      if (!this.data) return null
      const seq = ++weekSeq
      const resp = await upsertMyWeekPlan({ week: this.data.week.iso, ...payload })
      this.setWeek(resp.data.data, seq)
      return this.data
    },

    /** Sets the effort level of the entry's task (PATCH /api/v2/tasks/{id}); "none" unsets it. */
    async setEffort(task_id: string, level: string): Promise<void> {
      await upsertTask(task_id, { effort_level: level })
      await this.quietRefresh()
    },
  },
})
